
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { InventoryItemDetails } from '@/@types/responses/inventory_item/InventoryItemDetails'
import { InventoryItemService } from '@/services/inventoryItem.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { InventoryTypeFieldAndValueModel } from '@/@types/models/InventoryTypeFieldAndValueModel'
import { InventoryTypeFieldOption } from '@/@types/responses/inventory_type/InventoryTypeFieldOption'
import { InventoryTypeService } from '@/services/inventoryType.service'
import Dropdown from '@/components/shared/Dropdown.vue'
import { BuildStepService } from '@/services/buildStep.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import ManufacturerSearchMachine from '@/components/elements/ManufacturerSearchMachine.vue'
import SupplierSearchMachine from '@/components/elements/SupplierSearchMachine.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import { InventoryTypeDropdownOption } from '@/@types/responses/inventory_type/InventoryTypeDropdownOption'
import { InventoryTypeFieldValueDetails } from '@/@types/responses/inventory_type/InventoryTypeFieldValueDetails'
import NumericInput from '@/components/shared/NumericInput.vue'
import { CaliberService } from '@/services/caliber.service'
import Checkbox from '@/components/shared/Checkbox.vue'
import { InventoryTypeFieldValueMessage } from '@/@types/messages/InventoryTypeFieldValueMessage'
import { CreateInventoryItemMessage } from'@/@types/messages/CreateInventoryItemMessage'
import { UpdateInventoryItemMessage } from'@/@types/messages/UpdateInventoryItemMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'InventoryItem',
  components: {
    AuditInformation,
    Checkbox,
    CurrencyInput,
    Dropdown,
    LoadingPulse,
    LockingButton,
    ManufacturerSearchMachine,
    NumericInput,
    SupplierSearchMachine,
    TextInput
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildStepService = inject(PROVIDER_CONSTANTS.buildStepService) as BuildStepService
    const caliberService = inject(PROVIDER_CONSTANTS.caliberService) as CaliberService
    const inventoryItemService = inject(PROVIDER_CONSTANTS.inventoryItemService) as InventoryItemService
    const inventoryTypeService = inject(PROVIDER_CONSTANTS.inventoryTypeService) as InventoryTypeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildStepService,
      caliberService,
      inventoryItemService,
      inventoryTypeService,
      toast
    }
  },
  data() {
    let inventoryItem: InventoryItemDetails|undefined
    let unchangedInventoryItem: InventoryItemDetails|undefined

    const booleanOptions = [
      new DropdownOptionResponse('True', true, 'Yes'),
      new DropdownOptionResponse('False', true, 'No')
    ]

    return {
      booleanOptions,
      buildStepDropdownOptions: [] as DropdownOptionResponse<number>[],
      caliberDropdownOptions: [] as DropdownOptionResponse<number>[],
      inventoryItem,
      inventoryTypeDropdownOptions: [] as InventoryTypeDropdownOption[],
      loading: true,
      saving: false,
      unchangedInventoryItem
    }
  },
  computed: {
    fields(): InventoryTypeFieldAndValueModel[] {
      if (!this.inventoryItem || !this.inventoryItem.inventoryTypeId) return []

      const index = this.inventoryTypeDropdownOptions.findIndex(t => t.id === this.inventoryItem?.inventoryTypeId)
      if (index === -1) return []

      const inventoryTypeFieldAndValues = [] as InventoryTypeFieldAndValueModel[]

      const inventoryTypeFields = this.inventoryTypeDropdownOptions[index].inventoryTypeFields
      for (let i = 0; i < inventoryTypeFields.length; i++) {
        const fieldValue = this.inventoryItem?.inventoryTypeFieldValues.find(f => f.inventoryTypeFieldId === inventoryTypeFields[i].id)
        if (fieldValue !== undefined)
          inventoryTypeFieldAndValues.push(new InventoryTypeFieldAndValueModel(inventoryTypeFields[i], fieldValue))
      }

      return inventoryTypeFieldAndValues
    },
    inventoryTypeText(): string {
      if (!this.inventoryItem) return ''

      const index = this.inventoryTypeDropdownOptions.findIndex(t => t.id === this.inventoryItem?.inventoryTypeId)
      if (index === -1) return ''

      return this.inventoryTypeDropdownOptions[index].name
    },
    isDirty(): boolean {
      const a = this.inventoryItem
      const b = this.unchangedInventoryItem

      let changed = false
      let requiredFieldsFilledOut = true

      if (a?.inventoryTypeFieldValues && b?.inventoryTypeFieldValues) {
        const currentValues = a.inventoryTypeFieldValues
        const oldValues = b.inventoryTypeFieldValues

        for (let i = 0; i < currentValues.length; i++) {
          const currentValue = currentValues[i]

          const index = this.inventoryTypeDropdownOptions.findIndex(t => t.id === this.inventoryItem?.inventoryTypeId)
          if (index !== -1) {
            const inventoryTypeFields = this.inventoryTypeDropdownOptions[index].inventoryTypeFields
            const inventoryTypeField = inventoryTypeFields.find(f => f.id === currentValue.inventoryTypeFieldId)
            if (inventoryTypeField !== undefined && inventoryTypeField.isRequired)
              requiredFieldsFilledOut = requiredFieldsFilledOut && currentValue.value !== ''
          }
        }

        for (let i = 0; i < currentValues.length; i++) {
          const currentValue = currentValues[i]
          if ((oldValues.length === 0 && currentValue.value !== '') || (oldValues.length > 0 && currentValue.value !== oldValues[i].value)) {
            changed = true
            break
          }
        }
      }

      return (a?.sku !== b?.sku ||
             a?.totalQuantityOnHand !== b?.totalQuantityOnHand ||
             a?.price !== b?.price ||
             a?.isCoreProduct !== b?.isCoreProduct ||
             changed ||
             (this.fields.length === 0 && a?.id === 0)) && (a?.inventoryTypeId !== undefined && requiredFieldsFilledOut) && (a?.price !== undefined && a?.price >= 0 && a?.totalQuantityOnHand !== undefined && a?.totalQuantityOnHand >= 0)
    },
    titleText(): string {
      return !this.inventoryItem || this.inventoryItem.id === 0  ? 'Create Inventory Item' : 'Edit Inventory Item'
    }
  },
  watch: {
    'inventoryItem.inventoryTypeId'(): void {
      this.buildMissingFieldValues()
    },
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    Promise.all([
      this.getBuildSteps(),
      this.getCalibers(),
      this.getInventoryTypes()
    ]).then(() => {
      if (this.id !==  0) {
        this.getInventoryItem()
      } else {
        this.inventoryItem = new InventoryItemDetails(0)
        this.unchangedInventoryItem = JSON.parse(JSON.stringify(this.inventoryItem))
        this.loading = false
      }
    })
  },
  methods: {
    buildMissingFieldValues(): void {
      if (!this.inventoryItem) return

      const index = this.inventoryTypeDropdownOptions.findIndex(t => t.id === this.inventoryItem?.inventoryTypeId)
      if (index === -1) return

      if (!this.inventoryItem || this.inventoryItem.id === 0)
        this.inventoryItem.inventoryTypeFieldValues = []

      const inventoryTypeFields = this.inventoryTypeDropdownOptions[index].inventoryTypeFields
      inventoryTypeFields.forEach(inventoryTypeField => {
        const fieldValue = this.inventoryItem?.inventoryTypeFieldValues.find(f => f.inventoryTypeFieldId === inventoryTypeField.id)
        if (!fieldValue) {
          const inventoryTypeFieldValue = new InventoryTypeFieldValueDetails(0, inventoryTypeField.id, '')
          this.inventoryItem?.inventoryTypeFieldValues.push(inventoryTypeFieldValue)
        }
      })
    },
    getBuildSteps(): Promise<void> {
      return this.buildStepService.getBuildStepDropdownOptions().then(dropdownOptionsResponse => {
        this.buildStepDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build steps. Try reloading the page.')
        }
      })
    },
    getCalibers(): Promise<void> {
      return this.caliberService.getCaliberDropdownOptions().then(dropdownOptionsResponse => {
        this.caliberDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the calibers. Try reloading the page.')
        }
      })
    },
    getInventoryItem(): Promise<void> {
      return this.inventoryItemService.getInventoryItem(this.inventoryItem ? this.inventoryItem.id : this.id).then(inventoryItem => {
        this.inventoryItem = inventoryItem
        this.unchangedInventoryItem = JSON.parse(JSON.stringify(this.inventoryItem))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the inventory item. Try reloading the page.')
        }
      })
    },
    getInventoryTypes(): Promise<void> {
      return this.inventoryTypeService.getInventoryTypeDropdownOptions(undefined).then(dropdownOptionsResponse => {
        this.inventoryTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the inventory types. Try reloading the page.')
        }
      })
    },
    onSaveInventoryItemClicked(): void {
      if (!this.inventoryItem || !this.inventoryItem.inventoryTypeId) return

      this.saving = true
      
      const inventoryTypeFieldValueMessages = this.inventoryItem.inventoryTypeFieldValues.map((inventoryTypeFieldValueDetails: InventoryTypeFieldValueDetails) => new InventoryTypeFieldValueMessage(inventoryTypeFieldValueDetails.id, inventoryTypeFieldValueDetails.inventoryTypeFieldId, String(inventoryTypeFieldValueDetails.value)))
      if (this.id === 0) {
        const createInventoryItemMessage = new CreateInventoryItemMessage(this.inventoryItem.isCoreProduct, this.inventoryItem.price, this.inventoryItem.sku, this.inventoryItem.totalQuantityOnHand, this.inventoryItem.inventoryTypeId, inventoryTypeFieldValueMessages)
        this.inventoryItemService.createInventoryItem(createInventoryItemMessage).then(objectCreatedResponse => {
          if (!this.inventoryItem) return

          this.$router.replace(`/inventory/${objectCreatedResponse.id}`)
          this.saving = false
          this.toast.success('The inventory item was created successfully.')
          this.$router.push('/inventory')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the inventory item. Please try again.')
          }
        })
      } else {
        const updateInventoryItemMessage = new UpdateInventoryItemMessage(this.inventoryItem.isCoreProduct, this.inventoryItem.price, this.inventoryItem.sku, this.inventoryItem.totalQuantityOnHand, inventoryTypeFieldValueMessages)
        this.inventoryItemService.updateInventoryItem(this.id, updateInventoryItemMessage).then(() => {
          this.toast.success('The inventory item was updated successfully.')
          this.saving = false
          this.$router.push('/inventory')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the inventory item. Please try again.')
          }
        })
      }
    },
    optionsForDropdown(inventoryTypeFieldOptions: InventoryTypeFieldOption[]): DropdownOptionResponse<string>[] {
      const dropdownOptions = [] as DropdownOptionResponse<string>[]
      inventoryTypeFieldOptions.forEach(option => {
        dropdownOptions.push(new DropdownOptionResponse(option.id.toString(), option.active, option.value))
      })

      return dropdownOptions
    },
    updatePageTitle(): void {
      if (this.id !== 0 && this.inventoryItem)
        document.title = `${this.inventoryItem.name} - Short Action Customs, LLC`
    }
  }
})
